import { Link } from 'react-router-dom';
import { Phone, Truck, MapPin, Clock, CheckCircle, ChevronRight } from 'lucide-react';

// Imágenes de stock para servicio a obra
const FORKLIFT_IMAGE = "https://images.unsplash.com/photo-1763665814842-f2526387dfb4?w=800";
const WAREHOUSE_DOCK = "https://images.unsplash.com/photo-1764046155497-ad7e50737ffa?w=800";

const ServicioObraPage = () => {
  const zonas = [
    "Motril", "Salobreña", "Almuñécar", "Torrenueva", "Castell de Ferro",
    "La Herradura", "Vélez de Benaudalla", "Lobres", "Carchuna", "Calahonda",
    "Órgiva", "Lanjarón", "Dúrcal", "Padul", "Lecrín"
  ];

  const ventajas = [
    {
      icon: <Truck className="w-8 h-8" />,
      title: "Entrega en Obra",
      description: "Llevamos los materiales directamente al punto de trabajo. Descarga en lugar indicado."
    },
    {
      icon: <MapPin className="w-8 h-8" />,
      title: "Radio de 50 km",
      description: "Cobertura en toda la Costa Tropical y Valle de Lecrín desde nuestro almacén en Motril."
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Rapidez Garantizada",
      description: "Entregas en 24-48 horas. Pedidos urgentes el mismo día según disponibilidad."
    },
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: "Cumplimiento",
      description: "Más de 30 años cumpliendo plazos. Tu obra no se para por falta de material."
    }
  ];

  return (
    <div className="industrial-bg" data-testid="servicio-obra-page">
      {/* Hero */}
      <section className="bg-[#1A365D] py-16">
        <div className="section-container">
          <nav className="text-sm text-gray-400 mb-4" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2">
              <li><Link to="/" className="hover:text-white">Inicio</Link></li>
              <li><ChevronRight className="w-4 h-4" /></li>
              <li className="text-white">Servicio a Obra</li>
            </ol>
          </nav>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white uppercase mb-4">
            Servicio a Obra en Motril
          </h1>
          <p className="text-lg text-gray-300 max-w-3xl">
            Suministro de yeso laminado (pladur), yesos y materiales de construcción con entrega directa en tu obra. 
            Radio de 50 km desde Motril.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Text Content */}
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1A365D] uppercase mb-6">
                Entrega de Materiales de Construcción en Motril y Alrededores
              </h2>
              
              <div className="prose prose-lg text-[#4A5568] mb-8">
                <p>
                  En <strong>Fornes e Hijos</strong> entendemos que el tiempo es oro en la construcción. 
                  Por eso ofrecemos un servicio de <strong>entrega de materiales de construcción en Motril</strong> 
                  ágil, puntual y profesional. Llevamos el yeso laminado (pladur), yesos, aislamientos y todos los materiales 
                  que necesitas directamente a tu obra.
                </p>
                <p>
                  Nuestro servicio de <strong>suministro de yeso laminado en Motril</strong> cubre toda la Costa Tropical 
                  de Granada, incluyendo Salobreña, Almuñécar, Castell de Ferro y las poblaciones del Valle de Lecrín. 
                  Con un radio de acción de 50 kilómetros, garantizamos cobertura para la mayoría de obras de la zona.
                </p>
                <p>
                  También disponemos de servicio de <strong>recogida en almacén</strong>. Si prefieres pasar a buscar 
                  tu pedido, estamos en el Polígono Alborán de Motril, con fácil acceso y espacio para carga. 
                  Preparamos tu pedido para que la recogida sea lo más rápida posible.
                </p>
              </div>

              {/* Service Options */}
              <div className="bg-white p-6 rounded-sm border border-gray-200 mb-8">
                <h3 className="font-heading text-xl font-bold text-[#1A365D] mb-4">
                  Opciones de Servicio:
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#3182CE]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <Truck className="w-5 h-5 text-[#3182CE]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#1A365D]">Entrega en Obra</h4>
                      <p className="text-sm text-[#4A5568]">
                        Transporte con nuestro vehículo. Descarga en el punto indicado. 
                        Coste según distancia y volumen.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#3182CE]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-[#3182CE]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#1A365D]">Recogida en Almacén</h4>
                      <p className="text-sm text-[#4A5568]">
                        Polígono Alborán, Motril. Pedido preparado para carga rápida. 
                        Horario: L-V 07:30-13:30 / 15:30-18:00.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <a
                href="tel:858130213"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="servicio-cta-call"
              >
                <Phone className="w-5 h-5" />
                Solicitar Presupuesto de Entrega
              </a>
            </div>

            {/* Image */}
            <div>
              <img
                src={FORKLIFT_IMAGE}
                alt="Servicio de entrega de materiales de construcción en Motril"
                className="w-full h-auto rounded-sm shadow-lg"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Ventajas */}
      <section className="py-20 bg-white">
        <div className="section-container">
          <h2 className="font-heading text-3xl font-bold text-[#1A365D] uppercase text-center mb-12">
            Ventajas de Nuestro Servicio a Obra
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {ventajas.map((ventaja, index) => (
              <div
                key={index}
                className="text-center p-6"
                data-testid={`ventaja-${index}`}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#3182CE]/10 text-[#3182CE] rounded-sm mb-4">
                  {ventaja.icon}
                </div>
                <h3 className="font-heading text-xl font-bold text-[#1A365D] mb-2">
                  {ventaja.title}
                </h3>
                <p className="text-[#4A5568]">{ventaja.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Zonas de Cobertura */}
      <section className="py-20 industrial-bg">
        <div className="section-container">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-3xl font-bold text-[#1A365D] uppercase text-center mb-4">
              Zonas de Entrega
            </h2>
            <p className="text-lg text-[#4A5568] text-center mb-12">
              Realizamos entregas de yeso laminado (pladur) y materiales de construcción en las siguientes localidades 
              de Granada:
            </p>
            <div className="bg-white p-8 rounded-sm border border-gray-200">
              <div className="flex flex-wrap gap-3 justify-center">
                {zonas.map((zona, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 bg-[#F7FAFC] text-[#4A5568] rounded-sm border border-gray-200 text-sm"
                  >
                    {zona}
                  </span>
                ))}
              </div>
              <p className="text-center text-sm text-[#64748B] mt-6">
                ¿Tu localidad no aparece? Consúltanos, podemos valorar entregas fuera de nuestra zona habitual.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 bg-[#1A365D]">
        <div className="section-container text-center">
          <h2 className="font-heading text-3xl font-bold text-white uppercase mb-4">
            ¿Tienes una Obra en Marcha?
          </h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
            Llámanos y te preparamos el pedido. Entrega rápida y cumplimiento garantizado.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:858130213"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#1A365D] font-semibold px-6 py-3 rounded-sm hover:bg-gray-100 transition-colors"
              data-testid="servicio-final-cta-call"
            >
              <Phone className="w-5 h-5" />
              858 130 213
            </a>
            <Link
              to="/contacto"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white font-semibold px-6 py-3 rounded-sm hover:bg-white/10 transition-colors"
              data-testid="servicio-final-cta-contacto"
            >
              Enviar Consulta
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicioObraPage;
